import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { formatNumeral } from '@/utils/function'
import dayjs from 'dayjs'
import { Fragment, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { getPaymentDateOverview } from '../../services'
import {
    PaymentDateProductGroupRevenue,
    RevenueByPaymentDateParams,
} from '../../types'

interface PaymentDateOverviewTableProps {
    filters: RevenueByPaymentDateParams
}

const formatPrice = (value: number) => {
    if (!value || value === 0) return '-'
    return formatNumeral(value, 0)
}

const formatPercent = (value: number) => {
    if (!value || value === 0) return '0.00%'
    return `${formatNumeral(value, 2)}%`
}

const PaymentDateOverviewTable: React.FC<PaymentDateOverviewTableProps> = ({
    filters,
}) => {
    const { t } = useTranslation()

    const { data: overviewData, isLoading } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.REPORTS_BI_REVENUE_BY_PAYMENT_DATE,
            'overview-pivot',
            filters,
        ],
        queryFn: () => getPaymentDateOverview(filters),
        enabled: !!filters.from_date && !!filters.to_date,
        staleTime: 5 * 60 * 1000,
    })

    const { productGroups, months, grandTotal } = useMemo(() => {
        if (!overviewData) {
            return { productGroups: [], months: [], grandTotal: 0 }
        }
        return {
            productGroups: overviewData.product_groups,
            months: overviewData.months,
            grandTotal: overviewData.grand_total,
        }
    }, [overviewData])

    // Calculate column totals
    const columnTotals = useMemo(() => {
        const monthly: Record<
            string,
            { renewal: number; new_sale: number; upsell: number }
        > = {}
        let totalRenewal = 0
        let totalNewSale = 0
        let totalUpsell = 0

        months.forEach((month) => {
            monthly[month] = { renewal: 0, new_sale: 0, upsell: 0 }
        })

        productGroups.forEach((group) => {
            months.forEach((month) => {
                const monthData = group.monthly_data[month]
                if (monthData) {
                    monthly[month].renewal += monthData.renewal
                    monthly[month].new_sale += monthData.new_sale
                    monthly[month].upsell += monthData.upsell
                }
            })
            totalRenewal += group.total_renewal
            totalNewSale += group.total_new_sale
            totalUpsell += group.total_upsell
        })

        return { monthly, totalRenewal, totalNewSale, totalUpsell }
    }, [productGroups, months])

    if (isLoading) {
        return (
            <div className="space-y-2">
                <div className="h-8 w-full bg-gray-200 animate-pulse rounded" />
                <div className="h-64 w-full bg-gray-200 animate-pulse rounded" />
            </div>
        )
    }

    if (!productGroups.length || !months.length) {
        return (
            <div className="text-center py-8 text-gray-500 bg-white border rounded-sm">
                {t('no_data')}
            </div>
        )
    }

    return (
        <div className="space-y-2">
            {/* Legend */}
            <div className="flex flex-wrap items-center gap-4 text-xs">
                <span className="text-slate-600 font-medium">
                    {t('overview.legend')}:
                </span>
                <div className="flex items-center gap-1.5">
                    <span className="inline-block w-4 h-4 rounded bg-green-100 border border-green-300" />
                    <span className="text-slate-700">
                        {t('payment_type.renewal')}
                    </span>
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="inline-block w-4 h-4 rounded bg-blue-100 border border-blue-300" />
                    <span className="text-slate-700">
                        {t('payment_type.new_sale')}
                    </span>
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="inline-block w-4 h-4 rounded bg-amber-100 border border-amber-300" />
                    <span className="text-slate-700">
                        {t('payment_type.upsell')}
                    </span>
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="inline-block w-4 h-4 rounded bg-emerald-100 border border-emerald-300" />
                    <span className="text-slate-700">
                        {t('overview.total_summary')}
                    </span>
                </div>
            </div>

            {/* Table */}
            <div className="border border-gray-300 rounded-md overflow-hidden bg-white shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                        <thead>
                            {/* Main header row */}
                            <tr>
                                <th
                                    rowSpan={2}
                                    className="border border-gray-300 px-3 py-2.5 text-left font-medium sticky left-0 bg-slate-100 z-10 min-w-[50px] text-slate-700"
                                >
                                    STT
                                </th>
                                <th
                                    rowSpan={2}
                                    className="border border-gray-300 px-3 py-2.5 text-left font-medium sticky left-[50px] bg-slate-100 z-10 w-[350px] max-w-[350px] text-slate-700"
                                >
                                    {t('overview.service')}
                                </th>
                                {/* Monthly columns */}
                                {months.map((month) => (
                                    <th
                                        key={month}
                                        colSpan={3}
                                        className="border border-gray-300 px-2 py-2.5 text-center font-medium bg-blue-100 text-blue-800"
                                    >
                                        {dayjs(month).format('MM/YYYY')}
                                    </th>
                                ))}
                                {/* Total by type */}
                                <th
                                    rowSpan={2}
                                    className="border border-gray-300 px-2 py-2.5 text-center font-medium min-w-[90px] bg-green-50 text-green-700"
                                >
                                    {t('payment_type.renewal')}
                                </th>
                                <th
                                    rowSpan={2}
                                    className="border border-gray-300 px-2 py-2.5 text-center font-medium min-w-[90px] bg-blue-50 text-blue-700"
                                >
                                    {t('payment_type.new_sale')}
                                </th>
                                <th
                                    rowSpan={2}
                                    className="border border-gray-300 px-2 py-2.5 text-center font-medium min-w-[90px] bg-amber-50 text-amber-700"
                                >
                                    {t('payment_type.upsell') || 'Upsell'}
                                </th>
                                <th
                                    rowSpan={2}
                                    className="border border-gray-300 px-3 py-2.5 text-center font-medium bg-emerald-100 text-emerald-800 min-w-[120px]"
                                >
                                    {t('overview.total_revenue')}
                                </th>
                                <th
                                    rowSpan={2}
                                    className="border border-gray-300 px-3 py-2.5 text-center font-medium bg-emerald-100 text-emerald-800 min-w-[80px]"
                                >
                                    {t('overview.percentage')}
                                </th>
                            </tr>
                            {/* Sub-header row - type per month */}
                            <tr>
                                {months.map((month) => (
                                    <Fragment key={month}>
                                        <th className="border border-gray-300 px-2 py-1.5 text-center font-medium text-xs min-w-[90px] bg-green-50 text-green-600">
                                            {t('payment_type.renewal')}
                                        </th>
                                        <th className="border border-gray-300 px-2 py-1.5 text-center font-medium text-xs min-w-[90px] bg-blue-50/70 text-blue-600">
                                            {t('payment_type.new_sale')}
                                        </th>
                                        <th className="border border-gray-300 px-2 py-1.5 text-center font-medium text-xs min-w-[90px] bg-amber-50 text-amber-600">
                                            Upsell
                                        </th>
                                    </Fragment>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {productGroups.map(
                                (
                                    group: PaymentDateProductGroupRevenue,
                                    index: number
                                ) => {
                                    const percentage =
                                        grandTotal > 0
                                            ? (group.total_revenue /
                                                  grandTotal) *
                                              100
                                            : 0

                                    return (
                                        <tr
                                            key={group.product_group_name}
                                            className="hover:bg-slate-50 transition-colors"
                                        >
                                            <td className="border border-gray-300 px-3 py-2 text-center sticky left-0 bg-white">
                                                {index + 1}
                                            </td>
                                            <td
                                                className="border border-gray-300 px-3 py-2 sticky left-[50px] bg-white w-[350px] max-w-[350px]"
                                                title={group.product_group_name}
                                            >
                                                <span className="block truncate">
                                                    {group.product_group_name}
                                                </span>
                                            </td>
                                            {/* Monthly values by type */}
                                            {months.map((month) => {
                                                const monthData =
                                                    group.monthly_data[month]
                                                return (
                                                    <Fragment key={month}>
                                                        <td className="border border-gray-300 px-3 py-2 text-right bg-green-50/30">
                                                            {formatPrice(
                                                                monthData?.renewal ||
                                                                    0
                                                            )}
                                                        </td>
                                                        <td className="border border-gray-300 px-3 py-2 text-right">
                                                            {formatPrice(
                                                                monthData?.new_sale ||
                                                                    0
                                                            )}
                                                        </td>
                                                        <td className="border border-gray-300 px-3 py-2 text-right bg-amber-50/30">
                                                            {formatPrice(
                                                                monthData?.upsell ||
                                                                    0
                                                            )}
                                                        </td>
                                                    </Fragment>
                                                )
                                            })}
                                            {/* Total by type */}
                                            <td className="border border-gray-300 px-3 py-2 text-right font-medium bg-green-50/50 text-green-700">
                                                {formatPrice(
                                                    group.total_renewal
                                                )}
                                            </td>
                                            <td className="border border-gray-300 px-3 py-2 text-right font-medium bg-blue-50/50 text-blue-700">
                                                {formatPrice(
                                                    group.total_new_sale
                                                )}
                                            </td>
                                            <td className="border border-gray-300 px-3 py-2 text-right font-medium bg-amber-50/50 text-amber-700">
                                                {formatPrice(
                                                    group.total_upsell
                                                )}
                                            </td>
                                            {/* Total revenue */}
                                            <td className="border border-gray-300 px-3 py-2 text-right font-medium bg-emerald-50/50 text-emerald-700">
                                                {formatPrice(
                                                    group.total_revenue
                                                )}
                                            </td>
                                            {/* Percentage */}
                                            <td className="border border-gray-300 px-3 py-2 text-right font-medium bg-emerald-50/50 text-emerald-700">
                                                {formatPercent(percentage)}
                                            </td>
                                        </tr>
                                    )
                                }
                            )}
                            {/* Total row */}
                            <tr className="bg-slate-200 font-bold">
                                <td
                                    colSpan={2}
                                    className="border border-gray-300 px-3 py-2.5 text-center sticky left-0 bg-slate-200 text-slate-800 font-bold"
                                >
                                    {t('overview.total')}
                                </td>
                                {/* Monthly totals */}
                                {months.map((month) => {
                                    const mt = columnTotals.monthly[month]
                                    return (
                                        <Fragment key={month}>
                                            <td className="border border-gray-300 px-3 py-2.5 text-right bg-green-100/50 text-green-800">
                                                {formatPrice(mt?.renewal || 0)}
                                            </td>
                                            <td className="border border-gray-300 px-3 py-2.5 text-right bg-blue-100/50 text-blue-800">
                                                {formatPrice(mt?.new_sale || 0)}
                                            </td>
                                            <td className="border border-gray-300 px-3 py-2.5 text-right bg-amber-100/50 text-amber-800">
                                                {formatPrice(mt?.upsell || 0)}
                                            </td>
                                        </Fragment>
                                    )
                                })}
                                {/* Type totals */}
                                <td className="border border-gray-300 px-3 py-2.5 text-right bg-green-100 text-green-800">
                                    {formatPrice(columnTotals.totalRenewal)}
                                </td>
                                <td className="border border-gray-300 px-3 py-2.5 text-right bg-blue-100 text-blue-800">
                                    {formatPrice(columnTotals.totalNewSale)}
                                </td>
                                <td className="border border-gray-300 px-3 py-2.5 text-right bg-amber-100 text-amber-800">
                                    {formatPrice(columnTotals.totalUpsell)}
                                </td>
                                {/* Grand total */}
                                <td className="border border-gray-300 px-3 py-2.5 text-right bg-emerald-100 text-emerald-800">
                                    {formatPrice(grandTotal)}
                                </td>
                                <td className="border border-gray-300 px-3 py-2.5 text-right bg-emerald-100 text-emerald-800">
                                    100.00%
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}

export default PaymentDateOverviewTable
